import React, { useMemo } from "react";
import { GenericButton } from "@/components/GenericButton/GenericButton";
import "./IntegrationConnectionCard.scss";
import { useTranslation } from "react-i18next";

type Props = {
  icon: React.ReactNode;
  name: string;
  status:
    | "connected"
    | "not-connected"
    | "unavailable"
    | "other-tool-connected";
  hideStatus?: boolean;
  onAction?: () => void;
};

export const IntegrationConnectionCard: React.FC<Props> = ({
  icon,
  name,
  status,
  hideStatus,
  onAction,
}) => {
  const { t } = useTranslation();
  const statusLabel = t(`pages.integrations.connections.status.${status}`);

  const actionLabel = useMemo(() => {
    if (status === "not-connected") {
      return t("buttons.connect");
    }
    return t(`buttons.unavailable`);
  }, [status, t]);

  const isActionDisabled = useMemo(() => {
    return status === "unavailable" || status === "other-tool-connected";
  }, [status]);

  const statusColor = useMemo(() => {
    if (status === "connected") {
      return "green" as const;
    }
    return "grey" as const;
  }, [status]);

  return (
    <div
      className={`integration-connection-card ${isActionDisabled ? "disabled" : ""}`}
    >
      <div className="integration-connection-informations">
        <div className="integration-connection-informations-icon">{icon}</div>

        <div className="integration-connection-informations-text-container">
          <div className="integration-connection-informations-name">{name}</div>

          {!hideStatus ? (
            <div
              className={`integration-connection-informations-status-row ${statusColor}`}
            >
              <span className="integration-connection-informations-status-dot" />

              <span className="integration-connection-informations-status-label">
                {statusLabel}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {status !== "connected" && (
        <GenericButton
          label={actionLabel}
          onClick={onAction}
          disabled={isActionDisabled}
          theme="secondary"
        />
      )}
    </div>
  );
};
